import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import Button from "../../components/FormComponents/Button";
import Input from "../../components/FormComponents/Input";
import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import { useAppOrgContext } from "../../context/AppOrgContext";
import { useAuth0Context } from "../../context/Auth0Context";
import { useModalContext } from "../../context/ModalContext";
import useMutation from "../../hooks/useMutation";
import useQuery from "../../hooks/useQuery";
import Loader from "../../Layouts/Loader/Loader";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import {
  createOrganisationApi,
  deleteOrganisationApi,
  getOrganisationsApi,
  updateOrganisationApi
} from "../../services/apis/organisationApis";
import axiosInstance from "../../services/axiosService";
import "../../styles/AppOrgSection.css";
import { toastSuccess } from "../../utils/toastUtils";
import OrganizationCard from "./OrganizationCard";
import OrgModal from "./OrgModal";

const Organization = () => {
  return (
    <div className="application_container">
      <AppLeftSideBar type="org" />
      <div className="dashboard_outer_section">
        <OrganizationContent />
      </div>
    </div>
  );
};

const OrganizationContent = () => {
  const { data, isLoading, isFetching, error } = useQuery("organisations", ({ signal }) =>
    getOrganisationsApi({ signal })
  );

  if (isLoading) {
    return <Loader width={70} />;
  }

  if (error) {
    return (
      <div className="no_applications_found">
        <p className="search_result_text">Failed to load organisations. Please try again.</p>
      </div>
    );
  }
  return <OrganizationMain initialOrganisations={data?.data || []} isFetching={isFetching} />;
};

const themeAccessOnly = ["admin", "super-admin"];
const allowedRoles = ["super-admin", "admin", "mdm"];

const OrganizationMain = ({ initialOrganisations, isFetching }) => {
  const [organisations, setOrganisations] = useState(initialOrganisations);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const { getUserConfirmationForDelete } = useModalContext();
  const { setAppOrgnames, setAllApplications } = useAppOrgContext();
  const { user } = useAuth0Context();
  const navigate = useNavigate();

  const userCanCreateOrganisation = user?.role_names?.some((role) => themeAccessOnly.includes(role));

  const hasAccess = user?.role_names?.some((role) => allowedRoles.includes(role));

  // --- Mutations ---

  const { mutate: createOrg, isLoading: isCreating } = useMutation(createOrganisationApi, {
    onSuccess: (result) => {
      const { data, message } = result;

      const updatedData = {
        ...data,
        name: data?.name != null ? data.name.charAt(0).toUpperCase() + data.name.slice(1) : data?.name
      };

      setOrganisations((prev) => [...prev, updatedData]);
      toastSuccess(message);
      setIsModalOpen(false);
    }
  });

  const { mutate: updateOrg, isLoading: isUpdating } = useMutation(updateOrganisationApi, {
    onSuccess: (result, variables) => {
      setOrganisations((prev) => prev.map((org) => (org.id === variables.id ? { ...org, ...variables } : org)));

      toastSuccess(result.message);
      setIsModalOpen(false);
    }
  });

  const { mutate: deleteOrg, isLoading: isDeleting } = useMutation(deleteOrganisationApi, {
    onSuccess: (result, id) => {
      setOrganisations((prev) => prev.filter((org) => org.id !== id));

      toastSuccess(result.message);
    }
  });

  const addOrganization = (payload) => {
    createOrg(payload);
  };

  const editOrganization = (payload) => {
    updateOrg(payload);
  };

  const handleDelete = async (e, org) => {
    e.stopPropagation();
    const userConfirmed = await getUserConfirmationForDelete(org.name, "app");
    if (!userConfirmed) return;

    deleteOrg(org.id);
  };

  const openOrgAddModal = () => {
    setEditData({
      description: "",
      name: "",
      logo: ""
    });
    setIsModalOpen(true);
  };

  const openOrgEditModal = (e, org) => {
    e.stopPropagation();
    setEditData(org);
    setIsModalOpen(true);
  };

  const navigateToAppPage = (org) => {
    setAllApplications([]);
    setAppOrgnames((prev) => ({
      ...prev,
      orgName: org.name,
      orgLogo: org.logo
    }));
    navigate(`./${org.id}/app`);
  };

  const handleNavigatetoPipelines = () => {
    window.location.href = "/pipelines";
  };

  const handleDownloadGroupsAndRoles = async (org) => {
    try {
      const [groupsResponse, rolesResponse] = await Promise.all([
        axiosInstance.get(`/user-service/groups/org/${org.id}`),
        axiosInstance.get(`/user-service/role/org/${org.id}`)
      ]);

      const exportData = {
        organization: {
          id: org.id,
          name: org.name
        },
        groups: groupsResponse?.data,
        roles: rolesResponse?.data
      };

      const fileName = `${String(org.name || "organization")}-user-groups-and-roles.json`;

      const jsonBlob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: "application/json"
      });
      const downloadLink = document.createElement("a");

      downloadLink.href = URL.createObjectURL(jsonBlob);
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);

      toastSuccess("User groups and roles downloaded successfully");
    } catch (error) {
      console.error("Failed to download user groups and roles", error);
    }
  };

  const filteredOrganisations =
    organisations
      ?.filter((organisation) => organisation.name.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name)) || [];

  const isAnyMutationLoading = isCreating || isUpdating || isDeleting;

  return (
    <>
      <div className="dashboard-top-header">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h2>All Organizations</h2>
          {isFetching && <span style={{ fontSize: 12, opacity: 0.7 }}>(Refreshing...)</span>}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Search Input */}
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center"
            }}
          >
            <MaterialIcon
              name="search"
              style={{
                position: "absolute",
                left: "12px",
                fontSize: "18px"
              }}
            />
            <Input
              placeholder="Search Organization"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              inputBoxStyle={{
                paddingLeft: "36px",
                height: "36px",
                width: "240px"
              }}
            />
          </div>

          {/* Add Organization Button */}
          {userCanCreateOrganisation && (
            <Button
              variant="primary"
              onClick={openOrgAddModal}
              disabled={isAnyMutationLoading}
              style={{
                gap: "6px",
                fontSize: "14px",
                padding: "4px 12px",
                opacity: isAnyMutationLoading ? 0.7 : 1
              }}
            >
              <MaterialIcon name="add" style={{ fontSize: "24px" }} />
              Organization
            </Button>
          )}

          {/* Pipelines Button */}
          {hasAccess && (
            <Button
              variant="secondary"
              style={{
                gap: "6px",
                fontSize: "14px",
                padding: "4px 12px"
              }}
              onClick={handleNavigatetoPipelines}
            >
              <MaterialIcon name="alt_route" style={{ fontSize: "24px" }} />
              Pipelines
            </Button>
          )}
        </div>
      </div>

      <div className="org-dashboard-page-body-grid">
        {filteredOrganisations.length > 0 ? (
          filteredOrganisations.map((org) => (
            <OrganizationCard
              key={org.id}
              org={org}
              navigateToAppPage={navigateToAppPage}
              openOrgEditModal={openOrgEditModal}
              handleDelete={handleDelete}
              handleDownloadGroupsAndRoles={handleDownloadGroupsAndRoles}
            />
          ))
        ) : (
          <div className="no_applications_found">
            <p className="search_result_text">No organisations Found</p>
          </div>
        )}
      </div>

      <BuilderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        modalName={editData && editData.id !== undefined ? "Edit Organization" : "Add Organization"}
      >
        <OrgModal editData={editData} addOrganization={addOrganization} editOrganization={editOrganization} />
      </BuilderModal>
    </>
  );
};

export default Organization;
